import { useEffect, useRef, useState } from "react";
import { observer } from "mobx-react";
import ApiClient, { ApiError } from "../../backends/ApiClient";
import { useStores } from "../../stores/useStores";
import EditableField from "./EditableField";
import NotificationManager from "../notifications/NotificationManager";
import { useT } from "./TranslatedText";

function firstString(value: unknown): string | undefined {
  if (typeof value === "string") return value;
  if (Array.isArray(value) && typeof value[0] === "string") return value[0];
  return undefined;
}

// DRF returns either a field error ({email: [...]}) or a general one ({detail: "..."}).
function updateEmailErrorMessage(error: unknown): string | undefined {
  if (!(error instanceof ApiError)) return undefined;
  const { data } = error;
  if (typeof data !== "object" || data === null) return undefined;
  const email = "email" in data ? firstString(data.email) : undefined;
  const detail = "detail" in data ? firstString(data.detail) : undefined;
  return email ?? detail;
}

const ContactEmailField = observer(
  (props: {
    userId: string;
    email: string;
    organizationId?: string;
    directorySynced?: boolean;
    onSaved?: (email: string) => void;
    className?: string;
  }) => {
    const t = useT();
    const { userStore, organizationStore } = useStores();
    const [isEditing, setIsEditing] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [message, setMessage] = useState("");
    const [error, setError] = useState("");
    const identity =
      organizationStore.organization.json_data.settings?.identity;
    const emailSyncing =
      props.directorySynced &&
      identity?.provider &&
      identity[identity.provider]?.userFieldsToOverrideAndSync?.email;
    const isMounted = useRef(true);
    useEffect(() => {
      isMounted.current = true;
      return () => {
        isMounted.current = false;
      };
    }, []);

    async function save(value: string) {
      if (isSaving) return;
      if (value.trim() === (props.email || "")) {
        setIsEditing(false);
        return;
      }
      setIsSaving(true);
      setError("");
      try {
        const client = await ApiClient();
        const params = {
          path: {
            organization_pk:
              props.organizationId || organizationStore.organization.id,
            id: props.userId,
          },
        };
        if (userStore.id === props.userId) {
          const result = await client.POST(
            "/organizations/{organization_pk}/users/{id}/update_email/",
            { params, body: { email: value.trim() } },
          );
          if (!isMounted.current) return;
          setMessage(result.detail);
        } else {
          const result = await client.PATCH(
            "/organizations/{organization_pk}/users/{id}/",
            { params, body: { email: value.trim() } },
          );
          if (!isMounted.current) return;
          props.onSaved?.(result.email || "");
          NotificationManager.success(t("Contact email updated."));
        }
        setIsEditing(false);
      } catch (error) {
        if (!isMounted.current) return;
        setError(
          updateEmailErrorMessage(error) ??
            t("Unable to update email. Please try again."),
        );
      } finally {
        if (isMounted.current) setIsSaving(false);
      }
    }

    const placeholder = t("Enter contact email...");
    return (
      <div>
        <EditableField
          defaultValue={props.email || ""}
          isEditing={isEditing}
          onEditStart={() => {
            setError("");
            setMessage("");
            setIsEditing(true);
          }}
          onSave={save}
          onCancel={() => {
            if (!isSaving) {
              setIsEditing(false);
              setError("");
            }
          }}
          canEdit={!isSaving}
          inputType="email"
          placeholder={placeholder}
          className={props.className}
          hint={
            emailSyncing &&
            (identity.provider === "google"
              ? t(
                  "Signing in with Google, a force sync, or a directory update will replace this address with the directory email.",
                )
              : t(
                  "Signing in with Microsoft, a force sync, or a directory update will replace this address with the directory email.",
                ))
          }
        >
          <span>{props.email || placeholder}</span>
        </EditableField>
        {isSaving && <small role="status">{t("Saving…")}</small>}
        {error && <p role="alert">{error}</p>}
        {message && <p role="status">{message}</p>}
      </div>
    );
  },
);

export default ContactEmailField;
